import { Injectable } from '@angular/core';
import { SicorApiService } from '../SicorService/sicor-api.service';
import { tap, take } from 'rxjs/operators';
import { CamerasListRequest } from '../SicorService/RequestObject/CamerasListRequest';
import { Camera } from '../../models/Camera';
import { ImageFrmLogRequest } from '../SicorService/RequestObject/ImageFrmLogRequest';
import { DocsListRequest } from '../SicorService/RequestObject/DocsListRequest';
import { Document } from '../../models/Document';
import { CommandConnectorQueueInsRequest } from '../SicorService/RequestObject/CommandConnectorQueueInsRequest';
import { StorageService } from './storage.service';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { GlobalMethodsService } from '../global-methods.service';
import { TranslateConfigService } from 'src/app/services/commons/translate-config.service';
import { ModalController } from '@ionic/angular';
import { NavigationService } from './navigation.service';
import { CameraSettingsRequest } from '../SicorService/RequestObject/CameraSettingsRequest';
import { firstValueFrom } from 'rxjs';

const LAST_MEDIA_REQUEST_DATE = 'last-media-request-date';

@Injectable({
  providedIn: 'root'
})
export class CameraService {

  constructor(private sicorAPI: SicorApiService,
    private storage: StorageService,
    private gms: GlobalMethodsService,
    private translate: TranslateConfigService,
    private modalCtrl: ModalController,
    private navigation: NavigationService) { }

  async getCameras(user: string, s_ins: number): Promise<Camera[]>  {
    const cameraListRequest: CamerasListRequest = {
      user,
      s_ins
    };
    let camerasList: Camera[] = [];
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.camerasListRequest(cameraListRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null) {
              if (res.resultSet[0] != null && res.resultSet[0].error != null && res.resultSet[0].error === 401) {
                this.modalCtrl.dismiss(false, '', 'loadSpinner');
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else {
                if (res.resultSet[0].error != 0) {
                  for (let i = 0; i < res.resultSet.length; i++) {
                    let camera: Camera = new Camera();
                    camera.s_ins = res.resultSet[i].s_ins;
                    camera.deal_id = res.resultSet[i].deal_id;
                    camera.s_cam = res.resultSet[i].s_cam;
                    camera.desc_cam = res.resultSet[i].desc_cam;
                    camera.panel = res.resultSet[i].panel;
                    camera.camera_video = res.resultSet[i].camera_video;
                    camera.camera_voice = res.resultSet[i].camera_voice;
                    camera.det_typ = res.resultSet[i].det_typ;
                    camera.dwnl_skill = res.resultSet[i].dwnl_skill;
                    camera.zid = res.resultSet[i].zid;
                    camera.camera_url = res.resultSet[i].camera_url;
                    camerasList.push(camera);
                  }
                }
              }
            }
            resolve(camerasList);
          }, error: async e => {
            this.gms.openFailedProcessModal();
            resolve(null);
          }
        })
      ));
    });
  }

  public async downloadPhotoVideo(user: string, s_ins: number, doc_type: string, alid: string, comm_gen: string, originScreen: string): Promise<Document[]> {
    const requestParams: DocsListRequest = new DocsListRequest(user, s_ins, doc_type, alid, comm_gen);
    let resources: Document[] = [];
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.isAppUserDocsListRequest(requestParams)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null) {
              if (res.resultSet[0] != null && res.resultSet[0].error != null && res.resultSet[0].error === 401) {
                if (originScreen === 'historical') {
                  this.modalCtrl.dismiss(false, '', 'historicalModal');
                }
                this.modalCtrl.dismiss(false, '', 'loadSpinner');
                resolve(resources);
                await this.navigation.logout();
              }
              else {
                resources = res.resultSet;
                resolve(resources);
              }
            }
            else {
              resolve(resources);
            }
          }, error: e => {
            resolve(resources);
          }
        })
      ));
    });
  }

  public async newPhotoVideo(requestParams: CommandConnectorQueueInsRequest) {
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.cAppuserCommandConnectorQueueInsRequest(requestParams)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null && res.resultSet[0] != null && res.resultSet[0].error != null) {
              if (res.resultSet[0].error === 401) {
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else {
                resolve(true);
              }
            }
            else {
              resolve(false);
            }
          }, error: e => {
            resolve(false);
          }
        })
      ));
    });
  }

  public async getLastMediaRequestDate(s_ins: number) {
    let date = await this.storage.getItem(LAST_MEDIA_REQUEST_DATE + '_' + s_ins.toString());

    if (!date) {
      return new Date('2021-06-02T10:20:30Z').toString();
    }
    return date;
  }

  public async setLastMediaRequestDate(date: string, s_ins: number) {
    await this.storage.setItem(LAST_MEDIA_REQUEST_DATE + '_' + s_ins.toString(), date);
  }

  public async getCameraSettings(requestParams: CameraSettingsRequest) {
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.getCameraSettings(requestParams)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null && res.resultSet[0] != null) {
              resolve(res.resultSet[0]);
            }
            else {
              resolve(false);
            }
          }, error: e => {
            resolve(false);
          }
        })
      ));
    });
  }

  public async modifyCameraSettings(requestParams: CommandConnectorQueueInsRequest) {
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.cAppuserCommandConnectorQueueInsRequest(requestParams)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null && res.resultSet[0] != null && res.resultSet[0].error != null) {
              if (res.resultSet[0].error === 401) {
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else {
                resolve(true);
              }
            }
            else {
              resolve(false);
            }
          }, error: e => {
            resolve(false);
          }
        })
      ));
    });
  }

}
